/** Тройка: физическое время, логический счётчик, устройство.
 *
 *  `deviceId` остаётся частью отметки — на нём стоит `tick`, — но в
 *  сравнении журнальных строк он больше не участвует: два устройства,
 *  восстановленные из одной резервной копии, получают одинаковый
 *  `deviceId`, и тайбрейк по нему вернул бы ноль для двух разных строк.
 *  Полного порядка тогда нет, и устройства расходятся в том, какие пять
 *  попыток считать последними. Тайбрейк по `id` записи невозможен по
 *  построению: `id` уникален. */
export interface Hlc {
  ms: number;
  counter: number;
  deviceId: string;
}

/** Строка журнала в том виде, в каком её сравнивают: собственные часы плюс
 *  собственный идентификатор. Структурный тип, а не импорт из слоя данных:
 *  движок о хранилище не знает. */
export interface Sequenced {
  seq: Hlc;
  id: string;
}

/** Монотонно неубывающее стенное время. Часы, переведённые назад, отметку
 *  не откатывают — растёт счётчик. Ровно этим `seq` отличается от
 *  `presentedAt`, который откатывается вместе с системными часами: при
 *  порядке по стенному времени ошибка, сделанная после перевода даты
 *  назад, уезжает в начало истории, окно «последние пять» видит одни
 *  верные, и экран показывает «Освоено» через секунду после ошибки. */
export function tick(previous: Hlc | null, nowMs: number, deviceId: string): Hlc {
  /** Отравление часов необратимо, и в этом вся причина проверки.
   *
   *  `Math.max(NaN, что угодно)` — это `NaN`, а отметка ложится в состояние
   *  профиля и становится ПРЕДЫДУЩЕЙ для следующей. Одно нечисло — и часы
   *  этого ребёнка мертвы навсегда: порядок журнала перестаёт существовать,
   *  курсор отправки не сдвигается, слияние с другим устройством даёт
   *  бессмыслицу. Заметить это на экране нельзя вовсе — занятия идут.
   *
   *  Дорога сюда не выдуманная: отметка приходит из хранилища, а хранилище
   *  наполняется в том числе ЗАГРУЗКОЙ ФАЙЛА, который родитель мог получить
   *  откуда угодно и который никто не обязан был писать правильно. */
  /** Проверка идёт по БЕЗОПАСНОМУ целому, а не по «это число».
   *
   *  `Number.isFinite` пропускает то, что отравляет часы не хуже `NaN`:
   *  `1e300` — конечное число, и отметка навсегда уезжает в невозможное
   *  будущее, откуда стенное время её уже не догонит; `2 ** 53` проходит
   *  `Number.isInteger`, но прибавление единицы к нему НЕ МЕНЯЕТ значения —
   *  счётчик замирает, и две разные строки получают одну отметку, то есть
   *  порядок журнала перестаёт быть полным. Дробное время и отрицательный
   *  счётчик проходили тем же способом.
   *
   *  `Number.isSafeInteger` отсекает всё перечисленное разом: и `1e300`, и
   *  `2 ** 53`, и дробное. Отрицательный счётчик — отдельной строкой. */
  if (!Number.isSafeInteger(nowMs) || nowMs < 0) {
    throw new Error(`tick: время не безопасное целое (${String(nowMs)})`);
  }
  if (!previous) return { ms: nowMs, counter: 0, deviceId };
  const целая = Number.isSafeInteger(previous.ms) && previous.ms >= 0;
  const счётчик = Number.isSafeInteger(previous.counter) && previous.counter >= 0;
  if (!целая || !счётчик) {
    throw new Error(
      `tick: испорченная предыдущая отметка (ms=${String(previous.ms)}, counter=${String(previous.counter)})`,
    );
  }
  const ms = Math.max(previous.ms, nowMs);
  return { ms, counter: ms === previous.ms ? previous.counter + 1 : 0, deviceId };
}

/** Полный порядок журнальных строк: `(seq.ms, seq.counter, id)`.
 *
 *  Он детерминирован на любом наборе строк, вычисляется из самих строк и
 *  не требует обмена часами между устройствами. Тот же порядок обязан
 *  давать составной индекс `[profileId+seq.ms+seq.counter+id]`: от него
 *  зависит окно «последние пять» и статус «Освоен», и расхождение
 *  компаратора с индексом — не вопрос вкуса, а разные ответы на вопрос,
 *  освоен ли пример. */
export function compareSeq(a: Sequenced, b: Sequenced): number {
  if (a.seq.ms !== b.seq.ms) return a.seq.ms - b.seq.ms;
  if (a.seq.counter !== b.seq.counter) return a.seq.counter - b.seq.counter;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
